import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from "react-dom";
import { TEXTAREA_INPUTWRAPDIV_ID, TEXTAREA_TEXTINPUTDIV_ID, KEY_DELETE } from '@/constants';
import { getElementByQuerySelector, getActiveElement } from '@/helpers';
import { useKeyboardTyping } from '@/hooks';
import {
  Backdrop,
  InputWrapDiv,
  TextInputDiv,
} from './styles';

const EditableSectionContentTextarea = (props) => {
  const {
    text,
    visible,
    targetElementSelector,
    onChange,
    onFocus,
    onBlur,
    sectionAreaSize,
    inputOnType,
    selectedSection,
    scaleCoef,
    lineHeightCoef
  } = props;
  const textAreaStyles = {
    fontFamily: sectionAreaSize.fontFamily,
    fontStyle: sectionAreaSize.fontStyle,
    textDecoration: sectionAreaSize.textDecoration,
    fontSize: sectionAreaSize.fontSize,
    fontWeight: sectionAreaSize.fontWeight,
    color: sectionAreaSize.color,
    textAlign: sectionAreaSize.textAlign,
    alignItems: sectionAreaSize.alignItems,
    transform: sectionAreaSize.transform,
    lineHeight: lineHeightCoef,
    overflow: 'hidden',
    whiteSpace: sectionAreaSize.whiteSpace
  };
  const [localVisible, setLocalVisible] = useState(false);
  const [isStartedTyping, setStartedTyping] = useState(true);
  const [isFirstDoubleTap, setFirstDoubleTap] = useState(true);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [textareaSize, setTextareaSize] = useState({ height: 0, width: 0 });
  const inputRef = useRef(null);
  const initVisibleFnRef = useRef();
  const targetElement = getElementByQuerySelector(targetElementSelector);
  const isVisible = visible && localVisible;

  const handleKeyboardTyping = (event) => {
    if (inputRef.current) {
      if (event.target.innerHTML === 'Apply') {
        setLocalVisible(true);
        inputRef.current.blur();
        onBlur();
      } else {
        if (getActiveElement()?.nodeName === 'INPUT') {
          return; // this block is necessary
        } else if (getActiveElement()?.nodeName === 'TEXTAREA') {
          initVisibleFnRef.current();
          setTextareaSize(prev => ({ ...prev, height: 0 }));
          setTextareaSize({ height: inputRef.current.scrollHeight, width: inputRef.current.scrollWidth });
        } else {
          initVisibleFnRef.current();
        }
      }
    }
  }

  useKeyboardTyping({ handleKeyboardTyping });

  useEffect(() => {
    return () => {
      onBlur();
    }
  }, []);

  useEffect(() => {
    if (visible) {
      initVisibleFnRef.current();
    }
  }, [visible]);

  useEffect(() => {
    if (isVisible) {
      setTextareaSize(prev => ({ ...prev, width: inputRef.current.parentElement.scrollWidth ?? inputRef.current.scrollWidth }));
      setFirstDoubleTap(false);
      if (!isFirstDoubleTap) {
        setTextareaSize(prev => ({ ...prev, height: inputRef.current.scrollHeight }));
      }
    } else {
      setTextareaSize({ height: 0, width: 0 });
    }
    return () => {
      setFirstDoubleTap(true)
    }
  }, [isVisible, isFirstDoubleTap]);

  useEffect(() => {
    if (isVisible) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isVisible]);

  // will always create new instance of function with latest context. need for useEffect of 'keydown' subscriptions
  // need to use latest correct targetElementSelector value
  initVisibleFnRef.current = () => {
    // set text like this to fix wrong behavior with input cursor
    inputRef.current.value = text;
    inputRef.current.focus();
    // FIXME: wrong position for TM materials(ex. TC_US-TM-100.mat.json)
    
    if (targetElement) {
      const boundRect = targetElement.getBoundingClientRect();
      let transformStr = '';
      let top = boundRect.top;
      let left = boundRect.left;
      let height = boundRect.height;
      let width = boundRect.width;

      if (sectionAreaSize?.angle) {
        transformStr = `${transformStr} rotate(${sectionAreaSize.angle}deg)`;
        if (sectionAreaSize.angle === 90) {
          width = boundRect.height;
          height = boundRect.width;
          top = top + (width - height) / 2;
          left = left - (width - height) / 2;
        } else if (sectionAreaSize.angle === 270) {
          width = boundRect.height;
          height = boundRect.width;
          top = top - (width - height) / 2;
          left = left + (width - height) / 2;
        }
      }
      if (sectionAreaSize?.mirrorX) {
        transformStr = `${transformStr} scale(-1, 1)`
      }
      if (sectionAreaSize?.mirrorY) {
        transformStr = `${transformStr} scale(1, -1)`
      }
      setPosition({
        top,
        left,
        maxHeight: height,
        maxWidth: width,
        height,
        width,
        justifyContent: sectionAreaSize.textAlign,
        alignItems: sectionAreaSize.alignItems,
        transform: transformStr,
      });
    }

    setLocalVisible(true);
    onFocus();
  };

  const onBackdropClick = (e) => {
    setStartedTyping(true);
    setLocalVisible(false);
    inputRef.current.blur();
  };

  const handleChange = (e) => {
    if (isStartedTyping) {
      inputOnType(e.target.value);
      setStartedTyping(false);
    }
    onChange(e.target.value);
    resizeTarget({ height: e.target.scrollHeight, width: e.target.scrollWidth });
  };

  function resizeTarget(element) {
    if (element) {
      setTextareaSize(prev => ({ ...prev, height: 0 }));
      setTimeout(() => {
        setTextareaSize({ height: element.height, width: element.width });
      }, 0);
    }
  }

  const handleBlur = () => {
    onBlur();
    onBackdropClick();
  }

  const handleClick = (e) => {
    e.stopPropagation();
  }

  const handleKeyDown = (event) => {
    if (event.key === KEY_DELETE) {
      event.preventDefault();

      const input = inputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const value = input.value;

      // Handle selection or remove character after the cursor
      if (start === end) {
        // No selection, delete the character after the cursor
        if (start < value.length) {
          input.value = value.slice(0, start) + value.slice(start + 1);
          input.setSelectionRange(start, start);

          inputOnType(input.value);
        }
      } else {
        // If there is a selection, delete the selected text
        input.value = value.slice(0, start) + value.slice(end);
        input.setSelectionRange(start, start);

        inputOnType(input.value);
      }
    }
  };

  // TODO: refactoring: exclude portals to another component for better performance??
  return ReactDOM.createPortal(
    <Backdrop visible={isVisible}>
      <InputWrapDiv
        id={TEXTAREA_INPUTWRAPDIV_ID}
        position={position}
        justifyContent={sectionAreaSize.textAlign}
        alignItems={sectionAreaSize.alignItems}
      >
        <TextInputDiv
          id={TEXTAREA_TEXTINPUTDIV_ID}
          ref={inputRef}
          style={{ ...textAreaStyles, ...textareaSize }}
          placeholder="."
          rows={1}
          fontName="Lato"
          onChange={handleChange}
          onClick={handleClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </InputWrapDiv>
    </Backdrop>,
    getElementByQuerySelector('#floatable-section-content-textarea-portal'),
  );
};

export default React.memo(EditableSectionContentTextarea);
